/**
 * RefreshContext - Provides auto-refresh functionality for LiveDashboardV2
 * Adapted from old LiveDashboard RefreshContext
 */
import React, { createContext, useState, useEffect, useCallback, memo, useContext } from 'react'
import { Button, Spinner } from 'react-bootstrap'

// Create context for refresh state
export const RefreshContext = createContext(null)

// Custom hook to use refresh context
export const useRefresh = () => {
  const context = useContext(RefreshContext)
  if (!context) {
    throw new Error('useRefresh must be used within a RefreshProvider')
  }
  return context
}

// Provider component
export const RefreshProvider = ({
  children,
  interval = 120,
  onRefresh,
  isExternallyLoading = false
}) => {
  const [timeRemaining, setTimeRemaining] = useState(interval)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Reset timer to full interval
  const resetTimer = useCallback(() => {
    setTimeRemaining(interval)
  }, [interval])

  // Format time as MM:SS
  const getFormattedTime = useCallback(() => {
    const minutes = Math.floor(timeRemaining / 60)
    const seconds = timeRemaining % 60
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }, [timeRemaining])

  // Trigger manual refresh
  const triggerRefresh = useCallback(async () => {
    if (isRefreshing || isExternallyLoading) return

    setIsRefreshing(true)
    try {
      await onRefresh()
      resetTimer()
    } finally {
      setIsRefreshing(false)
    }
  }, [onRefresh, resetTimer, isRefreshing, isExternallyLoading])

  // Countdown timer effect — disabled when interval is null (WebSocket active)
  useEffect(() => {
    if (!interval) return

    const countdownInterval = setInterval(() => {
      if (!isExternallyLoading) {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            triggerRefresh()
            return interval
          }
          return prev - 1
        })
      }
    }, 1000)

    return () => clearInterval(countdownInterval)
  }, [interval, triggerRefresh, isExternallyLoading])

  const contextValue = {
    timeRemaining,
    formattedTime: getFormattedTime(),
    isRefreshing: isRefreshing || isExternallyLoading,
    triggerRefresh,
    resetTimer
  }

  return (
    <RefreshContext.Provider value={contextValue}>
      {children}
    </RefreshContext.Provider>
  )
}

// Countdown display component
export const CountdownDisplay = memo(() => {
  const { formattedTime } = useRefresh()
  return <span>{formattedTime}</span>
})

CountdownDisplay.displayName = 'CountdownDisplay'

// Refresh button component
export const RefreshButton = memo(({
  children,
  className,
  style,
  variant = 'info',
  disabled = false,
  ...props
}) => {
  const { triggerRefresh, isRefreshing } = useRefresh()

  return (
    <Button
      onClick={triggerRefresh}
      disabled={isRefreshing || disabled}
      className={className}
      style={style}
      variant={variant}
      {...props}
    >
      {isRefreshing ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
          />
          <span className="ms-1">Refreshing...</span>
        </>
      ) : (
        children || 'Refresh Now'
      )}
    </Button>
  )
})

RefreshButton.displayName = 'RefreshButton'

export default RefreshProvider
